import * as Safe from "safe-portals";
import { Serializers, Member } from "@flow/common/types";
import { sentMessageSerializer } from "@flow/core/shared/types";

// A purely documentary type -- provides no type safety
export type SubmissionGuid = string;

export const emailSubmissionPersonalDataSerializer = Safe.obj({
  member: Serializers.member,
  message: Safe.obj({
    subject: Safe.str,
    body: Safe.str,
  }),
});

export const responseSubmissionPersonalDataSerializer = Safe.obj({
  member_email: Safe.str,
  action_guid: Safe.uuid,
  who_responded: Safe.str,
  response: Safe.str,
  decision_maker_support: Safe.str,
});

export const videoSubmissionPersonalDataSerializer = Safe.obj({
  member: Serializers.member,
  message: Safe.obj({
    introduction: Safe.str,
  }),
});

// type is: 'send' | 'accepted' | 'rejected' | 'unmoderated'
export const submissionStatusSerializer = Safe.oneOf(
  "sent",
  "accepted",
  "rejected",
  "unmoderated",
  "test",
  "presend",
  "sending",
  "failed"
);

export type SubmissionStatus = Safe.TypeIn<typeof submissionStatusSerializer>;

export const submissionListItemSerializer = Safe.obj({
  guid: Safe.uuid,
  action_guid: Safe.uuid,
  status: submissionStatusSerializer,
  type: Safe.str,
  error_log: Safe.optional(Safe.str),
  created_at: Safe.dateIso,
  variant_idx: Safe.int,
  target_unique_ids: Safe.array(Safe.str),
  target_name: Safe.str,
  personal_data: Safe.variant(
    "email",
    emailSubmissionPersonalDataSerializer,
    "video",
    videoSubmissionPersonalDataSerializer
  ),
  video_url: Safe.optional(Safe.str), // only for 'video' type. XXX
});

export type SubmissionListItem = Safe.TypeIn<typeof submissionListItemSerializer>;

export const settingsSerializer = Safe.obj({
  s3_bucket: Safe.str,
  text_email_template: Safe.str,
  video_email_template: Safe.str,
  thank_you_email_template: Safe.str,
  reminder_email_template: Safe.str,
  consent_config_guids: Safe.optional(Safe.array(Safe.uuid)),
  video_consent_config_guids: Safe.optional(Safe.array(Safe.uuid)),
  target_response_consent_config_guids: Safe.array(Safe.uuid),
});

export type Settings = Safe.TypeIn<typeof settingsSerializer>;

export const responsesListItemSerializer = Safe.obj({
  action_guid: Safe.uuid,
  title: Safe.str,
  response_count: Safe.int,
  submission_count: Safe.int,
  created_at: Safe.dateIso,
});

export type ResponseListItem = Safe.TypeIn<typeof responsesListItemSerializer>;

export const reminderEmailParamsSerializer = Safe.obj({
  member_email: Safe.str,
  member_name: Safe.str,
  action_title: Safe.str,
  reminder_email_subject: Safe.str,
  reminder_email_body: Safe.str,
  reminder_email_footer: Safe.str,
  response_url: Safe.str,
  from_name: Safe.str,
  sent_message: sentMessageSerializer,
});
export type ReminderEmailParams = Safe.TypeIn<typeof reminderEmailParamsSerializer>;

export const emailStatsListItemSerializer = Safe.obj({
  target_email: Safe.str,
  total: Safe.int,
  rejected: Safe.int,
  opened: Safe.int,
});

export type EmailStatsListItem = Safe.TypeIn<typeof emailStatsListItemSerializer>;

export const responsesExportSerializer = Safe.obj({
  name: Safe.str,
  response: Safe.str,
  created_at: Safe.dateIso,
  consents: Safe.array(Serializers.consent),
});
export type ResponsesExport = Safe.TypeIn<typeof responsesExportSerializer>;

export const submissionsExportSerializer = Safe.obj({
  name: Safe.str,
  submission: Safe.str,
  created_at: Safe.dateIso,
  consents: Safe.array(Serializers.consent),
  target: Safe.array(Safe.str),
});
export type SubmissionsExport = Safe.TypeIn<typeof submissionsExportSerializer>;

export const selectedResponsesListItemSerializer = Safe.obj({
  action_guid: Safe.uuid,
  response: Safe.str,
  decision_maker_support: Safe.nullable(Safe.str),
  created_at: Safe.dateIso,
  target_unique_ids: Safe.str,
});

export type SelectedResponseListItem = Safe.TypeIn<typeof selectedResponsesListItemSerializer>;

export interface TextEmailParams {
  target: { name: string; email: string };
  member: Member;
  message: { subject: string; body: string };
  footer: string;
}

export interface VideoEmailParams {
  target: { name: string; email: string };
  member: Member;
  introduction: string;
  footer: string;
  thumbnailUrl: string;
  webVideoUrl: string;
}
